import { useState, type FormEvent } from "react";
import { useI18n } from "../../i18n/I18nProvider";
import { Button } from "../../components/ui/Button";
import { ErrorText } from "../../components/ui/feedback";
import { Input } from "../../components/ui/Input";
import { Modal } from "../../components/ui/Modal";

export interface NameModalProps {
  title: string;
  initial: string;
  submitLabel: string;
  busy: boolean;
  error: unknown;
  onSubmit: (name: string) => void;
  onClose: () => void;
}

/** Single-field dialog for naming a configuration (create or rename). Empty names are refused before sending. */
export function NameModal({
  title,
  initial,
  submitLabel,
  busy,
  error,
  onSubmit,
  onClose,
}: NameModalProps) {
  const { t } = useI18n();
  const [name, setName] = useState(initial);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed) onSubmit(trimmed);
  };
  return (
    <Modal title={title} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-note font-semibold text-ink-2">{t("configs.nameLabel")}</span>
          <Input
            value={name}
            maxLength={100}
            autoFocus
            placeholder={t("configs.namePlaceholder")}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <ErrorText error={error} />
        <div className="flex flex-wrap justify-end gap-2">
          <Button onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant="primary" disabled={busy || name.trim() === ""}>
            {submitLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
